import { RLayer, RSource, useMap } from "maplibre-react-components";
import React, { useEffect, useMemo } from "react";
import type { LineLayerSpecification, Map as MapLibreMap } from "maplibre-gl";
import { useTariffLayer } from "@/store/mode";
import { useSubscriptionStore } from "@/store/useSubscriptionStore";
import { useSelectedHexGroupStore } from "@/store/selectedHexGroup";
import { buildHexBorderFilter } from "@/utils/mapFilters";
import { isEdaTariffMode } from "@/types/base";
import { getTariffModeConfig } from "@/utils/tariffModeConfig";

const BORDER_ID = "hex-border";

const HexBorderLayer = () => {
  const map = useMap() as MapLibreMap | null;

  const { isSubscriptionActive } = useSubscriptionStore();
  const { selectedHexGroup } = useSelectedHexGroupStore();
  const tariffLayer = useTariffLayer((s) => s.tariffLayer);
  const isEdaMode = isEdaTariffMode(tariffLayer);
  const { borderColor } = getTariffModeConfig(tariffLayer);

  const tilesUrl = useMemo(() => {
    return [`${__MARTIN_URL__}/public.hexgroups/{z}/{x}/{y}.pbf`];
  }, []);

  useEffect(() => {
    if (!map) return;
    if (!map.getLayer(BORDER_ID)) return;

    const nextFilter = buildHexBorderFilter({
      isEdaMode,
      selectedHexGroupId: selectedHexGroup?.id,
    });

    map.setFilter(BORDER_ID, nextFilter);
  }, [isEdaMode, map, selectedHexGroup?.id]);

  const lineLayer = useMemo<LineLayerSpecification>(() => {
    return {
      id: BORDER_ID,
      type: "line",
      source: "hex-border-src",
      "source-layer": "public.hexgroups",
      minzoom: 5,
      filter: buildHexBorderFilter({ isEdaMode }),
      paint: {
        "line-color": borderColor,
        "line-width": 1,
      },
      layout: {
        "line-cap": "round",
        "line-join": "round",
      },
    };
  }, [borderColor, isEdaMode]);

  if (!isSubscriptionActive) return null;

  return (
    <>
      <RSource
        id="hex-border-src"
        type="vector"
        tiles={tilesUrl}
        minzoom={0}
        maxzoom={14}
      />

      <RLayer key="hex-border" {...lineLayer} />
    </>
  );
};

export default HexBorderLayer;
